import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate, CanDeactivate, ActivatedRouteSnapshot } from '@angular/router';
import { from, Observable, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { UserService } from 'services/user/user.service';

@Injectable()
export class AuthenticatedGuard implements CanActivate, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    private _isAuthenticated: boolean;

    constructor(private userService: UserService) {
        this._isAuthenticated = false; // Default to not authenticated.

        this.userService.UserSubject
            .pipe(takeUntil(this._subrelease))
            .subscribe(user => {
                this._isAuthenticated = user && (user.AuthToken != null && user.AuthToken !== '');
            }
        );
    }

    canActivate(route: ActivatedRouteSnapshot): boolean {
        // At a minimum, only allow access if we have a valid authtoken stored for the current user.
        if (!this._isAuthenticated) {
            return false;
        }

        //if no roles listed, route not setup correctly
        if (route.data == null || route.data['roles'] == null || route.data['roles'].length == 0) {
            return false;
        }

        // If no current role established, that is an automatic failure.
        if (this.userService.User.CurrentRole == null) {
            return false;
        }

        let rolesNeeded: string[] = route.data['roles'];

        //if current role doesn't match any roles in rolesNeeded
        if (!rolesNeeded.includes(this.userService.User.CurrentRole)) {
            return false;
        }

        //if nominee, need to see which nominee type they have
        if (this.userService.User.CurrentRole == "Nominee") {
            let nom = this.userService.User.Roles.find(x => x.RoleName == "Nominee")
            if (nom.NomineeType.toString() != route.params.NomineeType) {
                return false;
            }
        }

        //if designee, need to look at which rights they have
        if (this.userService.User.CurrentRole == "Designee") {
            let userRights: string[] = this.userService.User.Roles.find(u => u.RoleName == "Designee").Rights;
            let rightsNeeded: string[] = route.data['rights'];

            if (userRights == null || userRights.length == 0 || rightsNeeded == null || rightsNeeded.length == 0) {
                return false; //if rightsNeeded is empty, route not setup correctly
            }

            //if editing form, need to get which type of form
            if (rightsNeeded.includes("EDITFORMS")) {
                let award: any = route.params;

                if (award.system == "TOY" && award.NomineeType == "1") {
                    if (userRights.includes("EDITTOY") || userRights.includes("EDITFORMS")) {
                        return true;
                    }
                }
                else if (award.system == "TOY" && award.NomineeType == "2") {
                    if (userRights.includes("EDITSCHOOL") || userRights.includes("EDITFORMS")) {
                        return true;
                    }
                }
            }

            //does designee have rights needed?
            for (let n of rightsNeeded) {
                if (userRights.includes(n)) {
                    return true;
                }
            }

            return false; //is a designee, but rights do not match rights needed
        }

        return true;
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }
}

export interface UnsavedChanges {
    CanDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
    SetupTracking: () => void;
    TakeDownTracking: () => void;
}

@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChanges> {
    canDeactivate(target: UnsavedChanges) {
        return target.CanDeactivate ? target.CanDeactivate() : true;
    }
}

@Injectable()
export class UnsavedChangesService {
    private _nativeListener: EventListenerOrEventListenerObject;
    private _tracker: () => Observable<boolean> | Promise<boolean> | boolean;

    private _nativeChecker = function (checker, event) {
        const isClean = (checker != null ? checker(true) : false);

        if (!isClean) {
            const msg = 'There may be unsaved changes on this page. Do you wish to continue leaving the page?';

            event = event || window.event;

            if (event) {
                event.returnValue = msg;
            }

            return msg;
        } else {
            return;
        }
    };

    constructor() {
        this._nativeListener = null;
    }

    // #region Clear Tracking Method

    ClearTracking() {
        if (this._nativeListener) {
            window.removeEventListener('beforeunload', this._nativeListener);
            this._nativeListener = null;
        }

        this._tracker = null;
    }

    // #endregion

    // #region Check Tracker Method

    CheckTracker(): Observable<boolean> {
        const self = this;

        if (self._tracker != null) {
            const result = self._tracker();
            let obsResult: Observable<boolean> = null;

            if (result instanceof Promise) {
                obsResult = from(result);
            } else if (!(result instanceof Observable)) {
                obsResult = of<boolean>(result).pipe(take(1));
            } else {
                obsResult = <Observable<boolean>>result;
            }

            return obsResult;
        } else {
            return of<boolean>(true).pipe(take(1));
        }
    }

    // #endregion

    // #region Set Tracking Method

    SetTracking(tracker: () => Observable<boolean> | Promise<boolean> | boolean) {
        const self = this;

        if (self._nativeListener) {
            self.ClearTracking();
        }

        self._nativeListener = self._nativeChecker.bind(window, tracker);

        window.addEventListener('beforeunload', self._nativeListener);

        this._tracker = tracker;
    }

    // #endregion
}
